import moment from 'moment-timezone';

export type ViewersStatsVideoMeta = {
  id: string;
  channelId: string;
  startedAt: string;
  endedAt?: string;
};

/**
 * Minimal shape of the BFF country viewers payload used to build uPlot series.
 * Keeps this helper decoupled from the generated client export surface.
 */
export type ViewersStatsForChart = {
  metadata: {
    channels: Record<
      string,
      {
        id: string;
        title: string;
        isSelf?: boolean;
      }
    >;
    videos?: Record<string, ViewersStatsVideoMeta>;
  };
  timeSeries: Record<string, string | number>[];
};

/**
 * Converts per-minute viewer points (channel id keys) into uPlot aligned data for the given channel ids.
 * Only keys present in `metadata.channels` are treated as channel series (video ids are ignored).
 */
export function transformViewersStatsForUplot(
  stats: ViewersStatsForChart | undefined,
  visibleChannelIds: string[],
  timezone: string,
  pastelColors: string[],
) {
  const emptySeries: {
    label: string;
    stroke: string;
    show: boolean;
    spanGaps: boolean;
    width: number;
    points: { show: boolean };
  }[] = [];

  if (!stats?.timeSeries?.length || !visibleChannelIds.length) {
    return {
      data: [new Float64Array([]), new Float64Array([])] as unknown as number[][],
      series: emptySeries,
      seriesChannelIds: [] as string[],
    };
  }

  const channels = stats.metadata.channels;
  const orderedIds = [...visibleChannelIds]
    .filter((id) => channels[id])
    .sort((a, b) => {
      const ta = channels[a]?.title ?? a;
      const tb = channels[b]?.title ?? b;
      return ta.localeCompare(tb);
    });

  if (!orderedIds.length) {
    return {
      data: [new Float64Array([]), new Float64Array([])] as unknown as number[][],
      series: emptySeries,
      seriesChannelIds: [] as string[],
    };
  }

  const timeValues: number[] = [];
  const channelDataMap: Record<string, (number | null)[]> = {};

  orderedIds.forEach((id) => {
    channelDataMap[id] = [];
  });

  stats.timeSeries.forEach((row) => {
    const rawTime = row.time;
    if (typeof rawTime !== 'string') {
      return;
    }
    const timestamp = moment(rawTime).tz(timezone).valueOf() / 1000;
    timeValues.push(timestamp);

    orderedIds.forEach((channelId) => {
      const raw = row[channelId];
      const value =
        typeof raw === 'number' && Number.isFinite(raw) && !Number.isNaN(raw) ? raw : null;
      channelDataMap[channelId].push(value);
    });
  });

  const data: (number | null)[][] = [timeValues];
  orderedIds.forEach((channelId) => {
    data.push(channelDataMap[channelId]);
  });

  const series = orderedIds.map((channelId, index) => ({
    show: true,
    spanGaps: false,
    label: channels[channelId]?.title ?? channelId,
    stroke: pastelColors[index % pastelColors.length],
    width: 2,
    points: {
      show: false,
    },
  }));

  return { data: data as unknown as number[][], series, seriesChannelIds: orderedIds };
}
